import { ApprovalDoc, type Via } from "@kwp/contracts";
import { reviveTimestamps } from "@kwp/firestore";
import { z } from "zod";

export type Approval = ApprovalDoc & { id: string };

/** A stored approval, or null when it does not match the contract (it is then not shown). */
export function toApproval(id: string, data: unknown): Approval | null {
  const parsed = ApprovalDoc.safeParse(reviveTimestamps(data));
  return parsed.success ? { ...parsed.data, id } : null;
}

const ACTION_LABELS: Record<string, string> = {
  "mail.send": "Send an email",
  "mail.forward": "Forward an email",
  "mail.trash": "Move a message to Trash",
  "mail.modify": "Change a message",
  "mail.bulkTrash": "Move mail to Trash",
  "mail.bulkSpam": "Mark mail as spam",
  "mail.bulkModify": "Change mail in bulk",
  "mail.blockSender": "Block a sender",
  "rules.create": "Create a mail rule",
  "tasks.delete": "Delete a task",
};

/** The action's name in plain words ("mail.send" → "Send an email"). */
export function actionLabel(name: string): string {
  const known = ACTION_LABELS[name];
  if (known !== undefined) return known;
  const [area, verb] = name.split(".");
  if (area === undefined || verb === undefined) return name;
  const words = verb.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return `${area.charAt(0).toUpperCase()}${area.slice(1)}: ${words}`;
}

const VIA_LABELS: Record<Via, string> = {
  web: "You, in the web app",
  cli: "The kw command line",
  mcp: "An agent (MCP)",
  rule: "A mail rule",
  ai: "AI triage",
};

/** Who asked for (or did) something, from `requestedVia` / `actor.via`. */
export function viaLabel(via: Via): string {
  return VIA_LABELS[via];
}

const Person = z.union([z.string(), z.object({ name: z.string().nullable().optional(), email: z.string() })]);
type Person = z.infer<typeof Person>;

const SendPayload = z.object({
  accountId: z.string(),
  to: z.array(Person),
  cc: z.array(Person).default([]),
  bcc: z.array(Person).default([]),
  subject: z.string(),
  text: z.string().nullable().default(null),
  html: z.string().nullable().default(null),
});

const ForwardPayload = z.object({ messageId: z.string(), to: z.array(Person), note: z.string().default("") });

export function personLabel(p: Person): string {
  if (typeof p === "string") return p;
  return p.name === undefined || p.name === null || p.name.length === 0 ? p.email : `${p.name} <${p.email}>`;
}

/** How long a body preview may be before it is cut (the full payload is still the one sent). */
export const PREVIEW_CHARS = 2000;

/**
 * The text of an HTML body, for review only: parsed inertly (DOMParser runs no scripts and loads
 * nothing), scripts and styles dropped, and shown as plain text, never as HTML.
 */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, head").forEach((el) => el.remove());
  const text = doc.body.textContent;
  return text === null ? "" : text.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

function preview(text: string): string {
  return text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}…` : text;
}

export type Review =
  | { kind: "send"; from: string; to: string[]; cc: string[]; bcc: string[]; subject: string; body: string; bodyFromHtml: boolean; html: string | null }
  | { kind: "forward"; messageId: string; to: string[]; note: string }
  | { kind: "bulk"; matched: number; sample: string[] }
  | { kind: "json"; text: string };

/**
 * What the person reviews before approving: the exact payload, shaped per action. Anything this
 * screen does not recognise is shown as formatted JSON rather than hidden.
 * `addressOf` resolves a connected account id to its email address (null when unknown).
 */
export function reviewOf(approval: Pick<Approval, "action" | "payload" | "plan">, addressOf: (accountId: string) => string | null): Review {
  if (approval.plan !== null) return { kind: "bulk", matched: approval.plan.matched, sample: approval.plan.sample };
  if (approval.action === "mail.send") {
    const p = SendPayload.safeParse(approval.payload);
    if (p.success) {
      const address = addressOf(p.data.accountId);
      const fromHtml = p.data.text === null && p.data.html !== null;
      const raw = p.data.text !== null ? p.data.text : htmlToText(p.data.html === null ? "" : p.data.html);
      return {
        kind: "send",
        from: address === null ? p.data.accountId : address,
        to: p.data.to.map(personLabel),
        cc: p.data.cc.map(personLabel),
        bcc: p.data.bcc.map(personLabel),
        subject: p.data.subject,
        body: preview(raw),
        bodyFromHtml: fromHtml,
        html: p.data.html,
      };
    }
  }
  if (approval.action === "mail.forward") {
    const p = ForwardPayload.safeParse(approval.payload);
    if (p.success) return { kind: "forward", messageId: p.data.messageId, to: p.data.to.map(personLabel), note: preview(p.data.note) };
  }
  return { kind: "json", text: JSON.stringify(approval.payload, null, 2) };
}

/** "in 3 h", "in 12 min", or null once it has passed. */
export function timeLeft(expiresAt: Date, now: Date): string | null {
  const ms = expiresAt.getTime() - now.getTime();
  if (ms <= 0) return null;
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) return `in ${minutes} min`;
  return `in ${Math.round(minutes / 60)} h`;
}

export function isExpired(approval: Pick<Approval, "expiresAt">, now: Date): boolean {
  return approval.expiresAt.getTime() <= now.getTime();
}

/** Pending approvals still worth acting on (the badge count). */
export function countActionable(approvals: ReadonlyArray<Pick<Approval, "expiresAt">>, now: Date): number {
  return approvals.filter((a) => !isExpired(a, now)).length;
}

export function formatWhen(date: Date): string {
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
